import { useState, useEffect, useCallback } from 'react';
import {
  getPiiRules, createPiiRule, updatePiiRule, deletePiiRule, testPiiRules,
  testPiiPattern, generatePiiRegex,
  type PiiRule, type PiiRuleInput, type PiiPatternTestResult,
} from '../../api/admin';
import { useToastStore } from '../../stores/toast';

const EMPTY_FORM: PiiRuleInput = {
  name: '',
  category: '',
  pattern: '',
  replacement: '[REDACTED]',
  enabled: true,
  priority: 0,
  group_index: 0,
};

export default function PiiRulesTab({ embedded = false }: { embedded?: boolean } = {}) {
  const addToast = useToastStore((s) => s.addToast);
  const [rules, setRules] = useState<PiiRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PiiRuleInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [previewOut, setPreviewOut] = useState<string | null>(null);
  const [previewCounts, setPreviewCounts] = useState<{ category: string; count: number }[] | null>(null);
  const [previewing, setPreviewing] = useState(false);
  // Per-rule pattern test ( Part 1)
  const [testSample, setTestSample] = useState('');
  const [testOut, setTestOut] = useState<PiiPatternTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  // AI regex generator ( Part 2)
  const [aiOpen, setAiOpen] = useState(false);
  const [aiDesc, setAiDesc] = useState('');
  const [aiSamples, setAiSamples] = useState('');
  const [aiHints, setAiHints] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiExplain, setAiExplain] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getPiiRules();
      setRules(rows);
    } catch {
      addToast('Failed to load PII rules', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { void load(); }, [load]);

  const startEdit = (r: PiiRule) => {
    setEditingId(r.id);
    setForm({
      name: r.name, category: r.category, pattern: r.pattern,
      replacement: r.replacement, enabled: r.enabled,
      priority: r.priority, group_index: r.group_index,
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.category.trim() || !form.pattern.trim()) {
      addToast('Name, category, and pattern are required', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updatePiiRule(editingId, form);
        addToast('PII rule updated', 'success');
      } else {
        await createPiiRule(form);
        addToast('PII rule created', 'success');
      }
      resetForm();
      await load();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || 'Failed to save PII rule';
      addToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete PII rule "${name}"?`)) return;
    try {
      await deletePiiRule(id);
      addToast('PII rule deleted', 'success');
      if (editingId === id) resetForm();
      await load();
    } catch {
      addToast('Failed to delete PII rule', 'error');
    }
  };

  const handleToggleEnabled = async (r: PiiRule) => {
    try {
      await updatePiiRule(r.id, { enabled: !r.enabled });
      await load();
    } catch {
      addToast('Failed to toggle rule', 'error');
    }
  };

  const handlePreview = async () => {
    if (!previewText.trim()) {
      addToast('Enter sample text to preview', 'error');
      return;
    }
    setPreviewing(true);
    try {
      const res = await testPiiRules(previewText);
      setPreviewOut(res.redacted_text);
      setPreviewCounts(res.redactions);
    } catch {
      addToast('Preview failed', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleTestPattern = async () => {
    if (!form.pattern.trim()) {
      addToast('Enter a pattern to test', 'error');
      return;
    }
    if (!testSample.trim()) {
      addToast('Enter sample text to test against', 'error');
      return;
    }
    setTesting(true);
    setTestOut(null);
    try {
      const res = await testPiiPattern({
        pattern: form.pattern,
        text: testSample,
        group_index: form.group_index,
        replacement: form.replacement || '[REDACTED]',
      });
      setTestOut(res);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      addToast(detail || 'Pattern test failed (invalid pattern?)', 'error');
      setTestOut(null);
    } finally {
      setTesting(false);
    }
  };

  const handleGenerateRegex = async () => {
    if (!aiDesc.trim()) {
      addToast('Describe the PII type to generate a pattern', 'error');
      return;
    }
    setAiBusy(true);
    setAiExplain(null);
    try {
      const res = await generatePiiRegex({
        description: aiDesc.trim(),
        samples: aiSamples.trim() || undefined,
        hints: aiHints.trim() || undefined,
      });
      if (res.valid && res.pattern) {
        setForm({ ...form, pattern: res.pattern });
        setAiExplain(res.explanation || null);
        addToast('Regex generated and filled into Pattern field', 'success');
      } else {
        addToast(res.error || 'AI did not produce a valid pattern', 'error');
      }
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      addToast(detail || 'AI pattern generation failed', 'error');
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h2 className="text-lg font-semibold text-slate-800">PII Rules</h2>
          <p className="text-sm text-slate-500 mt-1">
            Configurable PII detectors used when scrubbing DB-sourced text
            before a cloud AI call. The default rules are seeded from
            the built-in detectors; add / edit / disable rules here. Both custom
            rules and the built-in detectors run together (defense in depth).
          </p>
        </div>
      )}

      {/* Edit / create form */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">
          {editingId ? 'Edit rule' : 'Add rule'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs text-slate-600">
            Name
            <input
              className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. SSN"
            />
          </label>
          <label className="text-xs text-slate-600">
            Category
            <input
              className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="e.g. ssn"
            />
          </label>
          <label className="text-xs text-slate-600 md:col-span-2">
            Pattern
            <input
              className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1 font-mono"
              value={form.pattern}
              onChange={(e) => setForm({ ...form, pattern: e.target.value })}
              placeholder="e.g. \b\d{3}-\d{2}-\d{4}\b"
            />
          </label>
          <label className="text-xs text-slate-600">
            Replacement
            <input
              className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1"
              value={form.replacement}
              onChange={(e) => setForm({ ...form, replacement: e.target.value })}
            />
          </label>
          <label className="text-xs text-slate-600">
            Priority (lower = first)
            <input
              type="number"
              className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
            />
          </label>
          <label className="text-xs text-slate-600">
            Group index (0 = whole match)
            <input
              type="number"
              className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1"
              value={form.group_index}
              onChange={(e) => setForm({ ...form, group_index: Number(e.target.value) })}
            />
          </label>
          <label className="text-xs text-slate-600 flex items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            Enabled
          </label>
        </div>

        {/* AI regex generator ( Part 2) */}
        <div className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50/50">
          <button
            type="button"
            onClick={() => setAiOpen(!aiOpen)}
            className="text-xs font-semibold text-slate-700 flex items-center gap-1"
          >
            <span>{aiOpen ? '\u25BC' : '\u25B6'}</span> AI pattern generator
            <span className="ml-1 text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">Local AI</span>
          </button>
          {aiOpen && (
            <div className="space-y-2">
              <p className="text-[11px] text-slate-500">
                Describe a PII type and the local AI will produce a
                pattern. Samples stay on-site \u2014 cloud backends are
                refused.
              </p>
              <label className="text-xs text-slate-600 block">
                Describe the PII type
                <input
                  className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1"
                  value={aiDesc}
                  onChange={(e) => setAiDesc(e.target.value)}
                  placeholder="e.g. US passport numbers"
                />
              </label>
              <label className="text-xs text-slate-600 block">
                Sample values (optional, one per line)
                <textarea
                  className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1 font-mono"
                  rows={3}
                  value={aiSamples}
                  onChange={(e) => setAiSamples(e.target.value)}
                  placeholder="Paste real sample values ONLY if the LOCAL model is in use"
                />
              </label>
              <label className="text-xs text-slate-600 block">
                Hints (optional)
                <input
                  className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1"
                  value={aiHints}
                  onChange={(e) => setAiHints(e.target.value)}
                  placeholder="e.g. case-insensitive, 9 digits with check digit"
                />
              </label>
              <button
                type="button"
                onClick={handleGenerateRegex}
                disabled={aiBusy}
                className="text-sm bg-emerald-700 text-white px-3 py-1.5 rounded-md disabled:opacity-50"
              >
                {aiBusy ? 'Generating...' : 'Generate pattern with AI'}
              </button>
            </div>
          )}
        </div>

        {aiExplain && (
          <p className="text-[11px] text-slate-500 italic">
            AI: {aiExplain}
          </p>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="text-sm bg-primary-600 text-white px-3 py-1.5 rounded-md disabled:opacity-50"
          >
            {editingId ? 'Update' : 'Create'}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="text-sm border border-slate-300 text-slate-700 px-3 py-1.5 rounded-md"
            >
              Cancel
            </button>
          )}
        </div>

        {/* Per-rule pattern test ( Part 1) */}
        <div className="border-t border-slate-200 pt-3 space-y-2">
          <p className="text-xs font-semibold text-slate-600">Test this pattern</p>
          <textarea
            className="w-full text-sm border border-slate-300 rounded-md px-2 py-1 font-mono"
            rows={2}
            value={testSample}
            onChange={(e) => setTestSample(e.target.value)}
            placeholder="Sample text to test the current pattern against (before saving)..."
          />
          <button
            type="button"
            onClick={handleTestPattern}
            disabled={testing}
            className="text-sm bg-slate-700 text-white px-3 py-1.5 rounded-md disabled:opacity-50"
          >
            {testing ? 'Testing...' : 'Test pattern'}
          </button>
          {testOut && (
            <div className="space-y-1">
              <p className={`text-xs font-semibold ${testOut.valid ? 'text-green-700' : 'text-red-700'}`}>
                {testOut.valid ? 'Valid pattern' : 'Invalid pattern'} \u00b7 {testOut.match_count} match{testOut.match_count === 1 ? '' : 'es'}
              </p>
              <pre className="text-xs bg-slate-50 border border-slate-200 rounded-md p-2 whitespace-pre-wrap font-mono">{testOut.redacted_text}</pre>
            </div>
          )}
        </div>
      </div>

      {/* Rules table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">Category</th>
              <th className="text-left px-3 py-2 font-medium">Pattern</th>
              <th className="text-left px-3 py-2 font-medium">Replacement</th>
              <th className="text-left px-3 py-2 font-medium">Priority</th>
              <th className="text-left px-3 py-2 font-medium">Enabled</th>
              <th className="text-left px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-3 py-4 text-slate-400">Loading...</td></tr>
            )}
            {!loading && rules.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-4 text-slate-400">No PII rules.</td></tr>
            )}
            {rules.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                <td className="px-3 py-2 text-slate-600">{r.category}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-600 max-w-xs truncate" title={r.pattern}>{r.pattern}</td>
                <td className="px-3 py-2 text-slate-600">{r.replacement}</td>
                <td className="px-3 py-2 text-slate-600">{r.priority}</td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => handleToggleEnabled(r)}
                    className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      r.enabled ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {r.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </td>
                <td className="px-3 py-2 space-x-2 whitespace-nowrap">
                  <button type="button" onClick={() => startEdit(r)} className="text-xs text-primary-700 hover:underline">Edit</button>
                  <button type="button" onClick={() => handleDelete(r.id, r.name)} className="text-xs text-red-600 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Preview */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">Preview</h3>
        <p className="text-xs text-slate-500">
          Test all enabled rules against sample text. Shows the redacted output
          and per-category match counts.
        </p>
        <textarea
          className="w-full text-sm border border-slate-300 rounded-md px-2 py-1 font-mono"
          rows={3}
          value={previewText}
          onChange={(e) => setPreviewText(e.target.value)}
          placeholder="Paste sample text containing PII..."
        />
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewing}
          className="text-sm bg-slate-700 text-white px-3 py-1.5 rounded-md disabled:opacity-50"
        >
          {previewing ? 'Previewing...' : 'Preview'}
        </button>
        {previewOut !== null && (
          <div className="space-y-2">
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1">Redacted output</p>
              <pre className="text-xs bg-slate-50 border border-slate-200 rounded-md p-2 whitespace-pre-wrap font-mono">{previewOut}</pre>
            </div>
            {previewCounts && previewCounts.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-1">Matches</p>
                <ul className="text-xs text-slate-600 space-y-0.5">
                  {previewCounts.map((c) => (
                    <li key={c.category}>{c.category}: {c.count}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}